import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import NativeSelect from "@mui/material/NativeSelect";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useEffect, useRef, type FormEvent } from "react";
import type { PublishContractVersionRequest } from "@bw/shared/types/generated";
import { useForm } from "react-hook-form";

import {
  contractTermsDraftSchema,
  type ContractTermsDraft,
} from "../contractTermsDraft";

const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

export interface ContractTermsEditorProps {
  defaultValues: ContractTermsDraft;
  isPending: boolean;
  publishFailed: boolean;
  publishFailureMessage?: string;
  onSubmit: (draft: ContractTermsDraft) => void;
  onViewHistory: () => void;
  bindingOptions?: readonly {
    binding: NonNullable<
      PublishContractVersionRequest["service_fee_bindings"]
    >[number];
    label: string;
  }[];
  bindingsLoading?: boolean;
  bindingsError?: string | null;
  onRefreshBindings?: () => void;
  /** Universal default terms cannot bind organization-scoped fee editions. */
  showFeeBindings?: boolean;
  /** Offers starting draft wording; it fills document fields from the form's fee and date. */
  documentDraft?: {
    label: string;
    fill: (values: ContractTermsDraft) => Partial<ContractTermsDraft>;
    /** Explains why the document text disagrees with the form, or null. */
    mismatch?: (values: ContractTermsDraft) => string | null;
  };
}

/** Fields the agreement draft fills programmatically; keep labels clear of their text. */
const SHRUNK_LABEL = { inputLabel: { shrink: true } } as const;

function helperProps(id: string) {
  return { id };
}

function inspectDraft(draft: ContractTermsDraft) {
  const result = contractTermsDraftSchema.safeParse(draft);
  const errors: Partial<Record<keyof ContractTermsDraft, string>> = {};
  if (!result.success) {
    for (const issue of result.error.issues) {
      const field = issue.path[0];
      if (typeof field === "string" && !(field in errors)) {
        errors[field as keyof ContractTermsDraft] = issue.message;
      }
    }
  }
  return { errors, isValid: result.success };
}

export function ContractTermsEditor({
  defaultValues,
  isPending,
  publishFailed,
  publishFailureMessage,
  onSubmit,
  onViewHistory,
  bindingOptions = [],
  bindingsLoading = false,
  bindingsError = null,
  onRefreshBindings,
  showFeeBindings = true,
  documentDraft,
}: ContractTermsEditorProps) {
  const { register, watch, getValues, setValue } = useForm<ContractTermsDraft>({
    resolver: zodResolver(contractTermsDraftSchema, undefined, {
      mode: "sync",
    }),
    mode: "onChange",
    defaultValues,
  });
  const frequency = watch("frequency");
  const inspection = inspectDraft(watch());
  const errors = inspection.errors;
  const usesWeekday = frequency === "weekly" || frequency === "biweekly";
  const usesMonthDay = frequency === "monthly";
  const selectedBindings = watch("serviceFeeBindings") ?? [];
  const documentContent = watch("documentContent");
  const approvalReference = watch("approvalReference");
  // The draft can be refreshed until someone edits the inserted text by hand.
  const insertedDraft = useRef<string | null>(null);
  const draftMismatch = documentDraft?.mismatch?.(watch()) ?? null;
  const originalDocument = useRef({
    content: defaultValues.documentContent,
    approvalReference: defaultValues.approvalReference,
  });
  useEffect(() => {
    const original = originalDocument.current;
    if (original.approvalReference && documentContent !== original.content &&
        approvalReference === original.approvalReference) {
      setValue("approvalReference", "", { shouldDirty: true, shouldValidate: true });
    }
  }, [approvalReference, documentContent, setValue]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isPending || bindingsLoading || bindingsError) return;
    if (documentDraft?.mismatch?.(getValues())) return;
    const result = contractTermsDraftSchema.safeParse(getValues());
    if (result.success) onSubmit(result.data);
  }

  return (
    <Paper
      component="form"
      variant="outlined"
      noValidate
      onSubmit={submit}
      sx={{ p: { xs: 2, sm: 3 } }}
    >
      <Stack spacing={3}>
        <Stack spacing={2}>
          <Box>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
              Exact agreement document
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
              Publish the reviewed text exactly as the organization will accept it. The server stores these UTF-8 bytes and their hash permanently.
            </Typography>
          </Box>
          <Alert severity="warning">
            A reference records an approval already obtained outside the app. It does not create legal approval. Do not publish draft or unreviewed wording.
          </Alert>
          {documentDraft && (!documentContent.trim() || documentContent === insertedDraft.current) ? (
            <Box>
              <Button
                size="small"
                variant="outlined"
                disabled={isPending || Boolean(errors.commissionPercent) || Boolean(errors.effectiveDate)}
                onClick={() => {
                  const fields = documentDraft.fill(getValues());
                  for (const [field, value] of Object.entries(fields)) {
                    setValue(field as keyof ContractTermsDraft, value as string, { shouldDirty: true, shouldValidate: true });
                  }
                  insertedDraft.current = fields.documentContent ?? null;
                }}
              >
                {documentDraft.label}
              </Button>
              <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
                Unreviewed draft wording with the commission rate and effective date below, so enter those first. Until you edit the text, insert it again to pick up a changed rate or date. Record counsel&apos;s approval before publishing.
              </Typography>
            </Box>
          ) : null}
          {draftMismatch ? (
            <Alert severity="error" role="alert">
              {draftMismatch} Update the agreement text before publishing.
            </Alert>
          ) : null}
          <TextField
            id="contract-document-content"
            label="Agreement text"
            multiline
            minRows={12}
            disabled={isPending}
            error={Boolean(errors.documentContent)}
            helperText={errors.documentContent ?? "Paste only reviewed text before publishing. Exact whitespace and punctuation are retained in the accepted copy."}
            {...register("documentContent")}
            slotProps={SHRUNK_LABEL}
          />
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" } }}>
            <TextField label="Template key" disabled={isPending} error={Boolean(errors.documentTemplateKey)} slotProps={SHRUNK_LABEL}
              helperText={errors.documentTemplateKey ?? "Stable document family identifier."} {...register("documentTemplateKey")} />
            <TextField label="Document version" disabled={isPending} error={Boolean(errors.documentTemplateVersion)} slotProps={SHRUNK_LABEL}
              helperText={errors.documentTemplateVersion ?? "Version shown in the retained copy."} {...register("documentTemplateVersion")} />
            <TextField label="Rendering version" disabled={isPending} error={Boolean(errors.documentRenderingVersion)} slotProps={SHRUNK_LABEL}
              helperText={errors.documentRenderingVersion ?? "Renderer used to produce this exact text."} {...register("documentRenderingVersion")} />
            <TextField label="Language" disabled={isPending} error={Boolean(errors.language)} slotProps={SHRUNK_LABEL}
              helperText={errors.language ?? "For example, en-US."} {...register("language")} />
            <TextField label="Legal-party reference" disabled={isPending} error={Boolean(errors.legalPartyReference)}
              helperText={errors.legalPartyReference ?? "Registered-entity record or review reference."} {...register("legalPartyReference")} />
            <TextField label="Jurisdiction references" disabled={isPending} error={Boolean(errors.jurisdictionReferences)}
              helperText={errors.jurisdictionReferences ?? "Comma- or line-separated review references."} {...register("jurisdictionReferences")} />
            <TextField label="Legal approval reference" disabled={isPending} error={Boolean(errors.approvalReference)}
              helperText={errors.approvalReference ?? "Counsel or authorized review record for these exact bytes."}
              {...register("approvalReference")} sx={{ gridColumn: { md: "1 / -1" } }} />
          </Box>
        </Stack>
        {showFeeBindings ? (
          <>
          <Divider />
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              Version-owned fee editions
            </Typography>
            {bindingsLoading ? (
              <Typography role="status">Loading fee editions…</Typography>
            ) : null}
            {bindingsError ? (
              <Alert severity="warning">
                {bindingsError}{" "}
                <Button size="small" onClick={onRefreshBindings}>
                  Refresh fee editions
                </Button>
              </Alert>
            ) : null}
            {bindingOptions.map(({ binding, label }) => {
              const same = (value: typeof binding) =>
                value.fee_policy_version_id === binding.fee_policy_version_id &&
                value.selector.service_key === binding.selector.service_key &&
                value.selector.origin === binding.selector.origin &&
                value.selector.purpose === binding.selector.purpose;
              return (
                <FormControlLabel
                  key={`${binding.fee_policy_version_id}:${binding.selector.purpose}`}
                  label={label}
                  control={
                    <Checkbox
                      disabled={
                        isPending || bindingsLoading || Boolean(bindingsError)
                      }
                      checked={selectedBindings.some(same)}
                      onChange={(_event, checked) =>
                        setValue(
                          "serviceFeeBindings",
                          checked
                            ? [
                                ...selectedBindings.filter(
                                  (value) =>
                                    !(
                                      value.selector.service_key ===
                                        binding.selector.service_key &&
                                      value.selector.origin ===
                                        binding.selector.origin &&
                                      value.selector.purpose ===
                                        binding.selector.purpose
                                    ),
                                ),
                                structuredClone(binding),
                              ]
                            : selectedBindings.filter((value) => !same(value)),
                          { shouldValidate: true, shouldDirty: true },
                        )
                      }
                    />
                  }
                />
              );
            })}
          </Stack>
          </>
        ) : null}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ alignItems: { sm: "flex-start" } }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
              Commercial terms
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
              Publish a dated commission and payout schedule version.
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            <Button
              type="button"
              variant="outlined"
              size="small"
              disabled={isPending}
              onClick={onViewHistory}
            >
              View history
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={
                isPending ||
                !inspection.isValid ||
                Boolean(draftMismatch) ||
                bindingsLoading ||
                Boolean(bindingsError)
              }
            >
              {isPending ? "Publishing…" : "Publish new version"}
            </Button>
          </Stack>
        </Stack>

        {publishFailed ? (
          <Alert severity="error" role="alert">
            {publishFailureMessage ?? (
              "Contract terms could not be published. Your draft is still here. " +
              "Check contract history before publishing again."
            )}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gap: 2.5,
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          }}
        >
          <TextField
            id="contract-commission-percent"
            label="Commission rate (%)"
            type="number"
            disabled={isPending}
            error={Boolean(errors.commissionPercent)}
            helperText={
              errors.commissionPercent ??
              "Percentage charged to the organization."
            }
            slotProps={{
              htmlInput: {
                min: "0",
                max: "99.99",
                step: "any",
                "aria-describedby": "contract-commission-percent-help",
              },
              formHelperText: helperProps("contract-commission-percent-help"),
            }}
            {...register("commissionPercent")}
          />

          <TextField
            id="contract-effective-date"
            label="Effective date"
            type="date"
            disabled={isPending}
            error={Boolean(errors.effectiveDate)}
            helperText={
              errors.effectiveDate ?? "The date this version takes effect."
            }
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: {
                "aria-describedby": "contract-effective-date-help",
              },
              formHelperText: helperProps("contract-effective-date-help"),
            }}
            {...register("effectiveDate")}
          />

          <FormControl fullWidth disabled={isPending}>
            <InputLabel variant="standard" htmlFor="contract-payout-frequency">
              Payout schedule
            </InputLabel>
            <NativeSelect
              id="contract-payout-frequency"
              inputProps={{
                "aria-describedby": "contract-payout-frequency-help",
              }}
              {...register("frequency")}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly</option>
              <option value="monthly">Monthly</option>
            </NativeSelect>
            <FormHelperText id="contract-payout-frequency-help">
              How often eligible balances are paid.
            </FormHelperText>
          </FormControl>

          <TextField
            id="contract-minimum-payout"
            label="Minimum payout (USD)"
            type="text"
            disabled={isPending}
            error={Boolean(errors.minimumPayout)}
            helperText={
              errors.minimumPayout ??
              "Balances below this amount roll into the next payout cycle."
            }
            slotProps={{
              htmlInput: {
                inputMode: "decimal",
                "aria-describedby": "contract-minimum-payout-help",
              },
              formHelperText: helperProps("contract-minimum-payout-help"),
            }}
            {...register("minimumPayout")}
          />

          {usesWeekday ? (
            <FormControl
              fullWidth
              disabled={isPending}
              error={Boolean(errors.dayOfWeek)}
            >
              <InputLabel variant="standard" htmlFor="contract-day-of-week">
                Day of week
              </InputLabel>
              <NativeSelect
                id="contract-day-of-week"
                inputProps={{ "aria-describedby": "contract-day-of-week-help" }}
                {...register("dayOfWeek")}
              >
                {WEEKDAYS.map((weekday, index) => (
                  <option key={weekday} value={String(index)}>
                    {weekday}
                  </option>
                ))}
              </NativeSelect>
              <FormHelperText id="contract-day-of-week-help">
                {errors.dayOfWeek ??
                  "The weekday that anchors this payout cycle."}
              </FormHelperText>
            </FormControl>
          ) : null}

          {usesMonthDay ? (
            <TextField
              id="contract-day-of-month"
              label="Day of month"
              type="number"
              disabled={isPending}
              error={Boolean(errors.dayOfMonth)}
              helperText={errors.dayOfMonth ?? "The monthly payout anchor."}
              slotProps={{
                htmlInput: {
                  min: "1",
                  max: "31",
                  step: "1",
                  "aria-describedby": "contract-day-of-month-help",
                },
                formHelperText: helperProps("contract-day-of-month-help"),
              }}
              {...register("dayOfMonth")}
            />
          ) : null}

          <TextField
            id="contract-payout-timezone"
            label="Timezone"
            disabled={isPending}
            error={Boolean(errors.timezone)}
            helperText={
              errors.timezone ??
              "Use the timezone that governs the payout date."
            }
            slotProps={{
              htmlInput: {
                "aria-describedby": "contract-payout-timezone-help",
              },
              formHelperText: helperProps("contract-payout-timezone-help"),
            }}
            {...register("timezone")}
          />
        </Box>
      </Stack>
    </Paper>
  );
}
